import { useState, useEffect, useCallback } from 'react'
import { useMutation } from "@tanstack/react-query"

import { showLoading } from 'services/util'
import checkmark from 'images/check-green.svg'

import { updateFixedItemMutation } from '../../graphql/Mutations'
import { graphql  } from '../../api'

import css from '../sass/createedit.module.scss'
import themeCss from '../../components/sass/theme.module.scss'
import UploadFiles from '../UploadFiles'
import { getImageUrl } from '../../util/images'
import { handleEscapeKey } from '../../components/utils'
import TextEditor from '../TextEditor'
import DuplicatedTitleWarning from '../DuplicatedTitleWarning'
import { normalizeRichTextForStorage } from '../auctionsadminutil'
import { getAuctionStatus } from '../..//util/dateString'


export default function FixedItemEdit({ auction, item, refreshAdminAuctionQuery, setHideCloseModalButton, setShowModal, showFixedEditItemModal, hideCloseModalButton }) {

  if (!setHideCloseModalButton) setHideCloseModalButton = () => {}

  const hasStarted = getAuctionStatus(auction.StartTime, auction.EndTime) === 'started'

  const [description, setDescription] = useState(item.Description)
  const [category, setCategory] = useState(item.Category)
  const [donor, setDonor] = useState(item.Donor)
  const [imageFilename, setImageFilename] = useState(item.ImageFilename)
  const [itemCode, setItemCode] = useState(item.ItemCode)
  const [purchasePrice, setPurchasePrice] = useState(item.PurchasePrice)
  const [totalItemsAvailable, setTotalItemsAvailable] = useState(item.TotalItemsAvailable)
  const [limitPerPerson, setLimitPerPerson] = useState(item.LimitPerPerson)
  const [title, setTitle] = useState(item.Title)
  const [value, setValue] = useState(item.Value)

  const [imageUrl, setImageUrl] = useState('')
  const [showEditImage, setShowEditImage] = useState(false)

  const [disableEdit, setDisableEdit] = useState(true)

  const [updateSuccess, setUpdateSuccess] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)
  
  const [isLimitPerPersonValid, setIsLimitPerPersonValid] = useState(true)
  const [isTotalItemsAvailableValid, setIsTotalItemsAvailableValid] = useState(true)

  const [updatedItem, setUpdatedItem] = useState()
  const [isImageSelected, setIsImageSelected] = useState(false)
  const [isImageUploaded, setIsImageUploaded] = useState(false)
  
  const italicLabel = '<i>text to italicize</i>'
  const categories = auction?.ItemCategories || []

  const isSrApproved = auction?.ReviewStatus === "Approved"

  const toggleModal = useCallback(() => {
      setShowModal(false)
    }, [setShowModal])
  
    useEffect(() => {
      const onKeyDown = (event) => handleEscapeKey(event, toggleModal)
      //add event listener for when modal is mounted
      document.addEventListener('keydown', onKeyDown)
      //clean up when modal is unmounted
      return () => document.removeEventListener('keydown', onKeyDown)
  
    }, [toggleModal])

  useEffect(() => {
    if (description !== item.Description ||
    category !== item.Category ||
    donor !== item.Donor ||
    imageFilename !== item.ImageFilename ||
    itemCode !== item.ItemCode ||
    purchasePrice !== item.PurchasePrice ||
    totalItemsAvailable !== item.TotalItemsAvailable ||
    limitPerPerson !== item.LimitPerPerson ||
    title !== item.Title ||
    value !== item.Value ||
    (item.Category === null && categories.length > 0)) {
      setDisableEdit(false)
    } else setDisableEdit(true)
  }, [item, description, category, donor, imageFilename, itemCode, purchasePrice, totalItemsAvailable, limitPerPerson, title, value])

  useEffect(() => {
    if (limitPerPerson === 0 || limitPerPerson === '0') setIsLimitPerPersonValid(false)
    else setIsLimitPerPersonValid(true)
  }, [limitPerPerson])

  useEffect(() => {
    if (totalItemsAvailable === 0 || totalItemsAvailable === '0') setIsTotalItemsAvailableValid(false)
    else setIsTotalItemsAvailableValid(true)
  }, [totalItemsAvailable])

  
  const {data: updateItemResponse, error, isPending, mutate: updateItem } = useMutation({
    mutationFn: (input) => {
      return graphql({query: updateFixedItemMutation, variables: {input: input} })
    },
  })

  useEffect(() => {
    setHideCloseModalButton(isImageSelected)
  }, [isImageSelected, setHideCloseModalButton])

  const imageUploadResult = (success) => {
    setIsImageUploaded(success)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

        //return before submitting for static comps
    // return

    const normalizedDescription = normalizeRichTextForStorage(description)
    
    const input = {
      FixedItemId: item.FixedItemId,
      Category: category,
      Title: title.trim(),
      ImageFilename: imageFilename.trim(),
      Donor: donor.trim(),
      PurchasePrice: purchasePrice,
      TotalItemsAvailable: totalItemsAvailable,
      LimitPerPerson: limitPerPerson,
      Description: normalizedDescription,
      Value: value
    }

    setUpdatedItem(input)
    updateItem(input)
  }

    // use effect for auction banner image
    useEffect(() => {
      const url = getImageUrl(auction?.AuctionCode, item.ItemCode, imageFilename)
      setImageUrl(url)
    }, [auction, item, imageFilename])

  useEffect(() => {
    showLoading(isPending)

    if (error) {
      console.log("Item not updated: ", error)
      console.log(error.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (updateItemResponse && !updateSuccess) {
      console.log(updateItemResponse)
      console.log('Item updated: ', updateItemResponse.data.updateFixedItem.FixedItemId)
      const newItem = {...updatedItem, FixedItemId: updateItemResponse.data.updateFixedItem.FixedItemId, ItemCode: item.ItemCode}
      setUpdatedItem(newItem)
      console.log('set updated item again with the new stuff', newItem)
      refreshAdminAuctionQuery()
      setUpdateSuccess(true)

      console.log('after set create success')
    }
  }, [updateItemResponse, error, isPending, updatedItem, item.ItemCode, refreshAdminAuctionQuery, updateSuccess])

  //use effect to enable submit button when all fields are filled
  useEffect(() => {  
    const hasDescription = Boolean(normalizeRichTextForStorage(description))

    if (title && hasDescription && value && purchasePrice && donor && itemCode && isLimitPerPersonValid && isTotalItemsAvailableValid) {
      setDisableSubmit(false)
    } else {
      setDisableSubmit(true)
    }

  }, [title, description, value, purchasePrice, donor, itemCode, isLimitPerPersonValid, isTotalItemsAvailableValid])

  // use effect to allow submitting null for total items available to allow for unlimited
  useEffect(() => {
    if (totalItemsAvailable === '') setTotalItemsAvailable(null)
  }, [totalItemsAvailable])

  // use effect to allow submitting null for limit per person to allow for unlimited
  useEffect(() => {
    if (limitPerPerson === '') setLimitPerPerson(null)
  }, [limitPerPerson])

  const toggleShowEditImage = () => {
    let toggled = !showEditImage
    setShowEditImage(toggled)
  }

  const handleCategoryChange = (e) => {
    if (e.target.value === "No category") setCategory(null)
    else setCategory(e.target.value)
  }

  if (updateSuccess) {
    return (
      <>
        <button className={`${themeCss['button']} ${themeCss['FilledYellow']} ${themeCss['centered-button']}`} onClick={()=>showFixedEditItemModal()} >Close Modal</button>
        <div className={css['success-container']}>
          <img className={css['success-checkmark']} src={checkmark} alt={'Payment Successful'}/>
          <p>Item updated successfully!</p>
        </div>
      </>
    )
  }
  
  if (showEditImage) {
    return (
      <div className={css['page-container']}>
        {!isImageSelected && !isImageUploaded &&
          <button className={`${themeCss['button']} ${themeCss['FilledBlue']} ${themeCss['centered-button']}`} onClick={()=>toggleShowEditImage()} >Back to Item Edit</button>}

        {isImageUploaded &&
          <>
            <button className={`${themeCss['button']} ${themeCss['FilledYellow']} ${themeCss['centered-button']}`} onClick={()=>showFixedEditItemModal()} >Close Modal</button>
            <div className={css['success-container']}>
              <img className={css['success-checkmark']} src={checkmark} alt={'Item pic uploaded successfully!'}/>
              <p id="imageUploadSuccessMessage">Your image has been uploaded successfully!</p>
            </div>
          </>}

        <UploadFiles imageType={'item'} auction={auction} item={item} refreshQuery={refreshAdminAuctionQuery} parentSubmitSuccess={imageUploadResult} setIsImageSelected={setIsImageSelected}/>
      </div>
    )
  }

  return (
    <div className={css['page-container']}>
      <form onSubmit={onSubmit}>
        <div className={css['editmodal-topbuttons']}>
          {!hideCloseModalButton && <button className={`${themeCss['button']} ${themeCss['FilledYellow']} ${themeCss['centered-button']}`} onClick={()=>showFixedEditItemModal()} >Close Modal</button>}
          <button className={`${themeCss['button']} ${themeCss['FilledBlue']} ${themeCss['centered-button']}`}
            disabled={disableSubmit || disableEdit} id="saveItemSubmit" type="submit" >Save Item</button>
        </div>
        <h2>Edit Item "{item.Title}" for {auction.Title}</h2>
        <p className={css['label-notice']}>"Total Items Available" and "Item Quantity Limit per person" can be left blank to allow for no limit for that value.</p>
        <p className={css['label-notice']}>All other fields (excluding Category) are required to edit an item.</p>
        <div className={`${css['item-container']}`} >
          <div className={css['create-item-input']}>
            <label>Title</label>
            <input id="itemTitleInput"
              type='text'
              placeholder='e.g. Tickets to L.A. Opera'
              value={title}
              disabled={isSrApproved}
              onChange={(e) => setTitle(e.target.value)}/>
            <DuplicatedTitleWarning title={title} items={auction?.FixedItems} item={item} />
          </div>
          <div className={css['create-item-input']}>
            <TextEditor value={description} onChange={setDescription} isReadOnly={isSrApproved} editorId="itemDescriptionInput" />
          </div>
          <div className={css['create-item-input']}>
            <label>Category <span className={css['acceptable-text']}>(Optional)</span></label>
            {categories.length > 0 && 
              <select
                id="itemCategorySelect"
                value={category}
                onChange={handleCategoryChange}
                className={css['create-item-input-dropdown']}
              >
                <option value={null}>No category</option>

                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>}
            {categories.length === 0 && <p className={css['acceptable-text']}>No categories created, add Categories in the Items & Donations tab.</p>}  
          </div>
          <div className={css['create-item-input']}>
            <label>Fair Market Value (in dollars)</label>
              <input id="itemFairMarketValueInput"
                type='number'
                placeholder='e.g. 120'
                min='1'
                value={value}
                disabled={isSrApproved}
                onChange={(e) => setValue(e.target.value)}/>
          </div>
          <div className={css['create-item-input']}>
            <label>Fixed Item Purchase Price (in dollars)</label>
              <input id="itemPurchasePriceInput"
                type='number'
                placeholder='e.g. 15'
                min='1'
                disabled={hasStarted || isSrApproved}
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}/>
          </div>
          <div className={css['create-item-input']}>
            <label>Total Items Available <span className={css['acceptable-text']}>(leave blank for unlimited)</span></label>
              <input id="itemTotalItemsAvailableInput"
                type='number'
                placeholder='e.g. 50'
                min='1'
                value={totalItemsAvailable}
                onChange={(e) => setTotalItemsAvailable(e.target.value)}/>
              {!isTotalItemsAvailableValid && <p className={css['label-notice']}>Value cannot be 0. Enter a positive integer or leave blank for Unlimited.</p>}
          </div>
          <div className={css['create-item-input']}>
            <label>Item Quantity Limit per person  <span className={css['acceptable-text']}>(leave blank for unlimited)</span></label>
              <input id="itemLimitPerPersonInput"
                type='number'
                placeholder='e.g. 50'
                min='1'
                value={limitPerPerson}
                onChange={(e) => setLimitPerPerson(e.target.value)}/>
              {!isLimitPerPersonValid && <p className={css['label-notice']}>Value cannot be 0. Enter a positive integer or leave blank for Unlimited.</p>}
          </div>
          <div className={css['create-item-input']}>
            <label>Item Number <span className={css['label-notice']}>(cannot be edited after initialization)</span>
            </label>
              <input id="itemCodeInput"
                type='number'
                placeholder='e.g. 5471'
                min='1'
                value={itemCode}
                disabled={true}
                onChange={(e) => setItemCode(e.target.value)}/>
          </div>
          <div className={css['create-item-input']}>
            <label>Donor</label>
              <input id="itemDonorInput"
                type='text'
                placeholder='e.g. UCLA Athletics'
                value={donor}
                onChange={(e) => setDonor(e.target.value)}/>
          </div>
          <div className={css['create-item-input']}>
            <label>Item Image Upload <span className={css['acceptable-text']}>(Accepted file types: .jpg, .jpeg, .png, .avif) </span></label>
            <p>Current filename: {imageFilename}</p>
              <div className={css['thumbnail-container']}>
                <img src={imageUrl} alt={`${item?.Title} thumbnail`} />
                <button className={`${themeCss['button']} ${themeCss['FilledBlue']} ${css['button-image-edit']}`} onClick={()=>toggleShowEditImage()} >Edit Image</button>
              </div>
            <p>(Image to be uploaded on next page)</p>
          </div>
        </div>
        <button className={`${themeCss['button']} ${themeCss['FilledBlue']} ${themeCss['centered-button']}`}
          disabled={disableSubmit || disableEdit} id="saveItemSubmit" type="submit" >Save Item</button>
      </form>
    </div>
  )
}
